'use strict';

// ---------- La llamada celular, en dúplex, por WebUSB ----------
//
// Port a navegador de src/duplex-linea.js + android-puente/Mic.java del
// proyecto Electron. La idea de fondo no cambia un ápice (ver
// ARQUITECTURA.md): un único proceso corre en el teléfono, lanzado con
// `app_process` como usuario `shell` (que sí tiene CAPTURE_AUDIO_OUTPUT), y
// sostiene las dos direcciones de la llamada a la vez:
//
//   stdin  → AudioTrack  → TYPE_TELEPHONY   lo que decimos al que llama
//   stdout ← AudioRecord ← VOICE_CALL       lo que dice el que llama
//
// Lo único que cambia es CÓMO se llega a ese proceso: en vez de spawnear el
// binario `adb` desde Node, se abre un socket ADB directo por WebUSB (ver
// adb-connection.js) y se corre el mismo `Duplex.java` ya compilado
// (android-puente/com/tevlr2/Duplex.java, sin tocar).

import { abrirShell, shellTexto, subirArchivo } from './adb-connection.js';
import { detectDigitInBlock, BLOCK_SIZE } from './dtmf.js';
import { TinyEmitter } from './tiny-emitter.js';
import { pcm16ToWavBlob } from './wav.js';

const DEX_DUPLEX = '/data/local/tmp/duplex.dex';
const DEX_MIC = '/data/local/tmp/mic.dex';
const RATE = 8000;
const CONFIRM_BLOCKS = 2;

/** Concatena Uint8Arrays (los "Buffer.concat" del original, en navegador). */
function concatBytes(a, b) {
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}

/**
 * Decodifica un archivo de audio (ogg, mp3, wav…) a PCM 16 bits / 8 kHz /
 * mono — el formato de la telefonía — usando el propio navegador. Es el
 * reemplazo de `ffmpeg -ar 8000 -ac 1 -f s16le` de la versión de escritorio.
 */
async function decodificarA8kMono(arrayBuffer) {
  const ctx = new (window.OfflineAudioContext || window.webkitOfflineAudioContext)(1, 1, RATE);
  // decodeAudioData resamplea automáticamente al sampleRate del contexto.
  const audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
  const floats = audioBuffer.getChannelData(0);
  const pcm = new Int16Array(floats.length);
  for (let i = 0; i < floats.length; i++) {
    const s = Math.max(-1, Math.min(1, floats[i]));
    pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return { pcm, duracionSegundos: audioBuffer.duration };
}

/** Sube duplex.dex y mic.dex al teléfono. Idempotente, se puede repetir. */
export async function instalarPuente(adb, { duplexDexBytes, micDexBytes, log = () => {} }) {
  log('Subiendo duplex.dex…');
  await subirArchivo(adb, duplexDexBytes, DEX_DUPLEX);
  log('Subiendo mic.dex…');
  await subirArchivo(adb, micDexBytes, DEX_MIC);
  log('Puente instalado en /data/local/tmp/.');
}

export function crearLineaDuplex({ adb, log = () => {} }) {
  const dtmf = new TinyEmitter();
  dtmf.running = false;
  dtmf.start = () => {};
  dtmf.stop = () => {};

  let proc = null; // { socket-like proceso de @yume-chan/adb, writer, lector cancelado }
  let stdoutListeners = new Set(); // funciones que reciben cada Uint8Array de audio entrante
  let reproduciendo = null; // { abortado, timer }
  let grabacionLlamada = null; // { chunks, desde }
  const oyentesFin = [];
  let silenciadoPorNosotros = false;

  // --- análisis DTMF continuo sobre lo que llega del teléfono ---
  let resto = new Uint8Array(0);
  let cand = null, repet = 0, ultimo = null;

  function analizar(chunk) {
    resto = concatBytes(resto, chunk);
    const bytes = BLOCK_SIZE * 2;
    while (resto.length >= bytes) {
      const bloque = resto.subarray(0, bytes);
      resto = resto.subarray(bytes);
      const view = new DataView(bloque.buffer, bloque.byteOffset, bloque.byteLength);
      const m = new Float64Array(BLOCK_SIZE);
      for (let i = 0; i < BLOCK_SIZE; i++) m[i] = view.getInt16(i * 2, true);
      const d = detectDigitInBlock(m);
      if (d) {
        if (d === cand) repet++; else { cand = d; repet = 1; }
        if (repet === CONFIRM_BLOCKS && d !== ultimo) {
          ultimo = d;
          dtmf.emit('digit', { digit: d, timestamp: Date.now() });
        }
      } else { cand = null; repet = 0; ultimo = null; }
    }
  }

  async function bombearStdout(stream) {
    const reader = stream.getReader();
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        if (!value || value.length === 0) continue;
        analizar(value);
        for (const fn of stdoutListeners) fn(value);
      }
    } catch {
      /* cerrado, nada que hacer */
    }
  }

  async function bombearStderr(stream) {
    const reader = stream.getReader();
    const decoder = new TextDecoder();
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        const t = decoder.decode(value).trim();
        if (/estado|ruta|error/i.test(t)) log('  [dx] ' + t.split('\n')[0]);
      }
    } catch {
      /* cerrado */
    }
  }

  async function arrancar() {
    if (proc) return;
    const proceso = await abrirShell(adb, ['CLASSPATH=' + DEX_DUPLEX, 'app_process', '/', 'com.tevlr2.Duplex']);
    proc = proceso;
    dtmf.running = true;
    bombearStdout(proceso.stdout);
    if (proceso.stderr) bombearStderr(proceso.stderr);
    proceso.exited.then(() => {
      if (proc === proceso) { proc = null; dtmf.running = false; }
    });
  }

  function detener() {
    detenerGrabacionLlamada();
    reactivarMic();
    if (reproduciendo) { reproduciendo.abortado = true; clearTimeout(reproduciendo.timer); reproduciendo = null; }
    if (proc) { const p = proc; proc = null; try { p.kill(); } catch { /* ya murió */ } }
    dtmf.running = false;
  }

  async function silenciarMic() {
    if (silenciadoPorNosotros) return true;
    try {
      const salida = await shellTexto(adb, ['CLASSPATH=' + DEX_MIC, 'app_process', '/', 'com.tevlr2.Mic', 'on']);
      silenciadoPorNosotros = true;
      log(/silenciado=true/.test(salida)
        ? 'Micrófono del teléfono silenciado (el flujo habla solo).'
        : 'Pedí silenciar el micrófono pero el teléfono no lo confirmó.');
      return true;
    } catch (err) {
      log(`No pude silenciar el micrófono: ${err.message}`);
      return false;
    }
  }

  async function reactivarMic() {
    if (!silenciadoPorNosotros) return true;
    try {
      await shellTexto(adb, ['CLASSPATH=' + DEX_MIC, 'app_process', '/', 'com.tevlr2.Mic', 'off']);
      silenciadoPorNosotros = false;
      log('Micrófono del teléfono reactivado.');
      return true;
    } catch (err) {
      log(`No pude reactivar el micrófono: ${err.message}`);
      return false;
    }
  }

  /** Escribe el WritableStream del proceso en trozos, respetando backpressure. */
  async function escribirEnStdin(pcm) {
    if (!proc) return;
    const writer = proc.stdin.getWriter();
    try {
      const bytes = new Uint8Array(pcm.buffer, pcm.byteOffset, pcm.byteLength);
      const TAM = 3200; // ~200ms por trozo a 8kHz/16bit/mono
      for (let i = 0; i < bytes.length; i += TAM) {
        if (reproduciendo && reproduciendo.abortado) break;
        await writer.write(bytes.subarray(i, i + TAM));
      }
    } finally {
      writer.releaseLock();
    }
  }

  const audioBridge = {
    getStatus() { return { running: Boolean(proc), config: { phoneCaptureSource: 'usb-duplex' }, filePlaying: null }; },
    setFilePlaybackEndedListener(fn) {
      oyentesFin.push(fn);
      return () => { const i = oyentesFin.indexOf(fn); if (i !== -1) oyentesFin.splice(i, 1); };
    },

    async playFile(rutaOArrayBuffer) {
      await arrancar();
      await audioBridge.stopFile();
      await silenciarMic();

      let arrayBuffer;
      if (typeof rutaOArrayBuffer === 'string') {
        const resp = await fetch(rutaOArrayBuffer);
        arrayBuffer = await resp.arrayBuffer();
      } else {
        arrayBuffer = rutaOArrayBuffer;
      }
      const { pcm, duracionSegundos } = await decodificarA8kMono(arrayBuffer);

      const estado = { abortado: false, timer: null };
      reproduciendo = estado;

      const terminar = () => {
        if (reproduciendo !== estado) return;
        reproduciendo = null;
        oyentesFin.slice().forEach((f) => f());
      };

      // Igual que el original: el final se calcula por la DURACIÓN real del
      // audio, no por cuándo terminamos de escribir en stdin (que puede ser
      // casi instantáneo comparado con el tiempo real de reproducción).
      estado.timer = setTimeout(terminar, Math.round(duracionSegundos * 1000) + 500);
      escribirEnStdin(pcm).catch((err) => log(`Error reproduciendo: ${err.message}`));
    },

    async stopFile() {
      if (!reproduciendo) return;
      const estado = reproduciendo;
      reproduciendo = null;
      estado.abortado = true;
      clearTimeout(estado.timer);
      oyentesFin.slice().forEach((f) => f());
    },

    async recordToFile({ seconds }) {
      await arrancar();
      const chunks = [];
      const onData = (c) => chunks.push(c);
      stdoutListeners.add(onData);
      await new Promise((r) => setTimeout(r, seconds * 1000));
      stdoutListeners.delete(onData);
      const blob = pcm16ToWavBlob(chunks, RATE);
      const nombre = `mensaje-${new Date().toISOString().replace(/[:.]/g, '-')}.wav`;
      descargarBlob(blob, nombre);
      return nombre;
    },
    stopRecording() {},
  };

  function descargarBlob(blob, nombre) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = nombre;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  }

  function iniciarGrabacionLlamada() {
    if (grabacionLlamada) return grabacionLlamada.nombre;
    arrancar();
    const nombre = `llamada-${new Date().toISOString().replace(/[:.]/g, '-')}.wav`;
    const chunks = [];
    const onData = (c) => chunks.push(c);
    stdoutListeners.add(onData);
    grabacionLlamada = { chunks, onData, nombre, desde: Date.now() };
    log(`Grabando la llamada completa en ${nombre}`);
    return nombre;
  }

  function detenerGrabacionLlamada() {
    if (!grabacionLlamada) return null;
    const { chunks, onData, nombre, desde } = grabacionLlamada;
    grabacionLlamada = null;
    stdoutListeners.delete(onData);
    const blob = pcm16ToWavBlob(chunks, RATE);
    descargarBlob(blob, nombre);
    const seg = Math.round((Date.now() - desde) / 1000);
    log(`Grabación de la llamada guardada: ${nombre} (${seg} s)`);
    return { nombre, segundos: seg };
  }

  dtmf.start = () => {
    arrancar();
    reactivarMic();
  };
  dtmf.stop = () => {};

  const phoneBridge = {
    async answer() { await shellTexto(adb, ['input', 'keyevent', 'KEYCODE_CALL']); },
    async reject() { detener(); await shellTexto(adb, ['input', 'keyevent', 'KEYCODE_ENDCALL']); },
  };

  return {
    audioBridge, dtmf, phoneBridge, arrancar, detener,
    iniciarGrabacionLlamada, detenerGrabacionLlamada,
  };
}
